export type Metric = {
  value: string
  label: string
  detail: string
}

export type Pillar = {
  title: string
  summary: string
  points: string[]
}

export type BoardSeat = {
  organization: string
  role: string
  note?: string
}

export type CareerRole = {
  title: string
  organization: string
  period: string
  note?: string
}

export type Award = {
  name: string
  issuer: string
  years: string
}

export type Reference = {
  name: string
  affiliation: string
  relationship: string
}

export type Engagement = {
  title: string
  venue: string
  timing: string
}

export const images = {
  portrait: 'https://cdn.magicpatterns.com/uploads/qdrAAjoCpxJUVhW1mkuHet/1744217931006.jpg',
  panel: 'https://cdn.magicpatterns.com/uploads/m1bmLSapar5xwDWhv3MYgk/Screenshot_2026-08-18_024125.png',
  keynote: 'https://cdn.magicpatterns.com/uploads/4n5LLohzjbhAWTPN64W6Nb/1715806018448.jpg',
  family: 'https://cdn.magicpatterns.com/uploads/c7J9pdbkrfTkurDh3WY6vT/IMG_3742.jpg',
  cat: 'https://cdn.magicpatterns.com/uploads/j8Bs6yXB5CPm1Ee56JCE6J/IMG_3751.jpg',
  offDuty: 'https://cdn.magicpatterns.com/uploads/8As9dLW8kQvkpUDruHkWTq/IMG_3957.jpg',
}

export const profile = {
  name: 'Samanntha DuBridge',
  title: 'SVP, Chief Talent Officer — Hewlett Packard Enterprise',
  subheader:
    'Global People Executive · Fiduciary Steward ($24B Assets) · Enterprise AI & Future of Work',
  summary:
    'Driving enterprise-wide talent strategy, AI-driven workforce modernization, and fiduciary governance across a $30B+ global technology leader with 65,000 employees in 70 countries.',
  bio: [
    'Samanntha DuBridge leads talent for one of the world\u2019s foundational technology companies. Her mandate spans executive succession, total rewards, workforce AI, and the fiduciary governance of a $24B pension portfolio \u2014 the full arc of decisions a board must see clearly.',
    'She is known in the boardroom for translating human capital into financial language: run-rate savings, key-person risk, integration readiness, and workforce capacity modeled years ahead of demand. She has done this through a company split, more than a dozen acquisitions, and the arrival of generative AI in the workplace.',
  ],
  location: 'San Francisco Bay Area, California',
  contactNote: 'Contact information for references provided upon request.',
}

export const navItems = [
  { label: 'Overview', to: '/' },
  { label: 'Board Pillars', to: '/pillars' },
  { label: 'Impact', to: '/impact' },
  { label: 'Board Service', to: '/board-service' },
  { label: 'Speaking', to: '/speaking' },
  { label: 'Recognition', to: '/recognition' },
  { label: 'Career', to: '/career' },
  { label: 'Personal', to: '/personal' },
]

export const metrics: Metric[] = [
  {
    value: '$24B',
    label: 'Fiduciary Pension Portfolio',
    detail: 'Named fiduciary oversight of global pension and retirement assets.',
  },
  {
    value: '$5.5B+',
    label: 'Annual Program Budget',
    detail: 'Direct oversight of enterprise people and benefits spend.',
  },
  {
    value: '120K+',
    label: 'Employees Integrated via M&A',
    detail: 'Across 63 countries through acquisition, divestiture, and integration.',
  },
  {
    value: '< 1 Min',
    label: 'AI Service Routing',
    detail: 'Reduced from 1.5 days via an automated People Care model.',
  },
]

export const pillars: Pillar[] = [
  {
    title: 'Fiduciary Stewardship & Fiscal Optimization',
    summary: 'Named fiduciary discipline applied to multi-billion dollar people investments.',
    points: [
      'Direct oversight of a $5.5B+ program budget and a $4B global benefits portfolio.',
      'Captured $15M+ in recurring, sustainable run-rate savings through plan redesigns across 70+ countries.',
    ],
  },
  {
    title: 'AI Innovation & Digital Modernization',
    summary: 'Translating enterprise AI ambition into measurable workforce outcomes.',
    points: [
      'Pioneered an industry-first \u201cdigital twin\u201d AI workforce analytics and talent modeling framework.',
      'Consolidated 7 legacy service lines into an automated \u201cPeople Care\u201d model, reducing resolution routing from 1.5 days to under 1 minute.',
    ],
  },
  {
    title: 'C-Suite Succession & Talent Architecture',
    summary: 'Trusted counsel to the CEO, Board, and Compensation Committee.',
    points: [
      'Strategic advisor to the CEO, Board, and Compensation Committee on executive talent and pay.',
      'Designed an attribute-based executive succession framework across 12 capabilities, doubling internal mobility and mitigating key-person risk.',
    ],
  },
  {
    title: 'Global M&A & Cross-Border Integration',
    summary: 'Human capital diligence and integration at historic scale.',
    points: [
      'Built HPE\u2019s centralized HR M&A practice from the ground up.',
      'Led human capital due diligence and post-merger integration for the historic HP split, the 100K+ EDS integration, Silver Peak, Zerto, and Juniper Networks.',
    ],
  },
]

export const boardService: BoardSeat[] = [
  {
    organization: 'SemperVirens VC',
    role: 'Advisory Board Member & Angel Investor',
    note: 'Early-stage work, health, and financial technology.',
  },
  {
    organization: 'Airvet',
    role: 'Strategic Advisor',
    note: 'Benefits innovation and consumer health delivery.',
  },
  {
    organization: 'Value-Based Insurance Design Board',
    role: 'Board Member — Harvard-led initiative',
    note: 'Evidence-based benefit design and cost stewardship.',
  },
  {
    organization: 'LinkedIn Learning',
    role: 'Advisory Board Member',
    note: 'Enterprise skilling and workforce capability building.',
  },
]

export const priorEngagements: BoardSeat[] = [
  { organization: 'UnitedHealthcare & Optum', role: 'Advisory Board' },
  { organization: 'Anthem', role: 'Advisory Board' },
  { organization: 'Dorsal Musculoskeletal', role: 'Advisory Board' },
]

export const governanceMemberships: BoardSeat[] = [
  {
    organization: 'National Association of Corporate Directors (NACD)',
    role: 'Active Member',
    note: 'Ongoing director education and governance practice.',
  },
]

export const upcomingEngagements: Engagement[] = [
  {
    title: 'Strategic Workforce Planning and Talent Intelligence',
    venue: 'People Analytics World',
    timing: 'September 2026',
  },
  {
    title: 'The Future of Hiring — Driving Workforce Innovation in an AI-Driven World',
    venue: 'Host, Breakfast Symposium for CHROs',
    timing: 'September 2026',
  },
  {
    title: 'Shaping the Future of Work — Trends, Technology and Talent',
    venue: 'From Day One',
    timing: 'December 2026',
  },
]

export const pastEngagements: Engagement[] = [
  {
    title:
      'Navigating the Revolution in HR: How People Leaders Can Be Empowered in an Era of AI and Rapid Change',
    venue: 'From Day One — Live Conference, Silicon Valley',
    timing: '2026',
  },
  {
    title: 'Reshaping the Workforce: How AI and HR Technology Change How Things Get Done',
    venue: 'From Day One — Live Conference, Silicon Valley',
    timing: '2026',
  },
  {
    title: 'Keynote on Modern Total Rewards',
    venue: 'Silicon Valley Employers Forum',
    timing: '2025',
  },
  {
    title: 'Rediscovering Community Amidst Changing Corporate Values',
    venue: 'From Day One — Live Conference, San Francisco',
    timing: 'Conference',
  },
  {
    title: 'The Future of Benefits in an Evolving Workplace',
    venue: 'From Day One — Webinar Panel',
    timing: 'Panel',
  },
  {
    title: 'Reimagining the Post-Pandemic Workplace',
    venue: 'Healthcare Revolution Masterclass',
    timing: 'Masterclass',
  },
  {
    title: 'Comprehensive Well-Being Strategies',
    venue: 'Healthcare Revolution Conference',
    timing: 'Conference',
  },
]

export const mediaFeatures: Engagement[] = [
  {
    title: 'Samanntha DuBridge, HPE\u2019s Newest SVP, Balances Innovation and Simplicity',
    venue: 'Profile Magazine',
    timing: 'Interview',
  },
  {
    title: 'Samanntha DuBridge Keeps HPE Connected to Culture',
    venue: 'Profile Magazine',
    timing: 'Feature',
  },
  {
    title: 'Here\u2019s Why HPE Expanded its Paid Parental Leave Policy and Never Looked Back',
    venue: 'Fortune',
    timing: 'Interview',
  },
  {
    title: 'How Samanntha DuBridge Sparks Conversations About Mental Health at HPE',
    venue: 'American Healthcare Leader',
    timing: 'Interview',
  },
  {
    title: 'Paid Parental Leave and Culture',
    venue: 'JUST Capital',
    timing: 'Interview',
  },
  {
    title: 'Employee Recognition and Innovation at HPE',
    venue: 'New Practices Weekly Podcast',
    timing: 'Podcast',
  },
  {
    title: 'The Great Reset — Reshaping Benefits and Modernizing Workplace Culture',
    venue: 'Rebels with a Heart Podcast',
    timing: 'Podcast',
  },
  {
    title: 'Talent Acquisition and AI',
    venue: 'Call HR Podcast',
    timing: 'Podcast',
  },
]

export const careerHistory: CareerRole[] = [
  {
    title: 'SVP, Chief Talent Officer',
    organization: 'Hewlett Packard Enterprise',
    period: '2025 – Present',
    note: 'Enterprise talent strategy, AI workforce modernization, and executive succession.',
  },
  {
    title: 'VP, Global Culture, Employee Experience & Operations',
    organization: 'Hewlett Packard Enterprise',
    period: '2023 – 2025',
    note: 'Consolidated global people service delivery into an automated care model.',
  },
  {
    title: 'VP, Global Compensation, Benefits & M&A',
    organization: 'Hewlett Packard Enterprise',
    period: '2018 – 2022',
    note: 'Fiduciary oversight of pension, benefits, and acquisition integration.',
  },
  {
    title: 'VP, Global Benefits & Employee Mobility',
    organization: 'Hewlett Packard Enterprise',
    period: '2015 – 2018',
  },
  {
    title: 'Executive HR & Benefits Leadership Roles',
    organization: 'Uber · National Semiconductor · ALZA Pharmaceuticals · Kaiser Permanente',
    period: '1988 – 2015',
  },
]

export const education = {
  institution: 'San Jos\u00e9 State University',
  degrees: 'M.A. & B.S. — Healthcare Administration & Business Administration',
}

export const awards: Award[] = [
  {
    name: 'Most JUST and Ethical Companies',
    issuer: 'JUST Capital',
    years: '2024 & 2025',
  },
  { name: '100 Best Companies to Work For', issuer: 'Fortune', years: 'Multiple years' },
  { name: 'Global Mobility Team of the Year', issuer: 'Industry recognition', years: 'Multiple years' },
  {
    name: 'Excellence in Health and Well-being',
    issuer: 'Best Employers',
    years: 'Multiple years',
  },
  {
    name: 'Healthy Lifestyle Platinum Award',
    issuer: 'Best Employer',
    years: 'Multiple years',
  },
  {
    name: 'Force for Good — Senior Executive',
    issuer: 'Hewlett Packard Enterprise internal award',
    years: 'Awarded',
  },
  {
    name: 'North American Employee Engagement Award',
    issuer: 'Employee Engagement Awards',
    years: 'Awarded',
  },
]

export const references: Reference[] = [
  {
    name: 'Kristin Major',
    affiliation: 'Chief People Officer, NVIDIA',
    relationship: 'Former Chief People Officer, HPE',
  },
  {
    name: 'Alan May',
    affiliation: 'UW Foster School of Business · Bar Eagle Holdings LLC',
    relationship: 'Former CHRO, HPE',
  },
  {
    name: 'Kathy Winters',
    affiliation: 'CHRO, Arcadia eFuels',
    relationship: 'Executive peer',
  },
]

export const personalQuotes = [
  {
    quote:
      'Culture is not a program. It is what people experience on their hardest day, and whether the company shows up for them.',
    context: 'On why wellbeing sits at the center of her work',
  },
  {
    quote:
      'I have never been able to separate the human from human capital. The numbers only hold if the people behind them do.',
    context: 'On fiduciary responsibility',
  },
]

export const personalNotes = [
  {
    title: 'Home in the Bay Area',
    body:
      'Home is a Northern California house full of half-finished projects, good food, and the kind of Sunday mornings that never quite go to plan. Her husband is the household\u2019s resident builder, cook, and enthusiastic tester of whatever new pan has just arrived.',
    image: images.family,
    caption: 'Weekend project energy — the household\u2019s resident cook and builder.',
  },
  {
    title: 'The smallest member of the household',
    body:
      'Every long day of board decks and global calls ends the same way: with a cat who has claimed the warmest square of sunlight in the house and expects the tennis ball to be thrown at least once more.',
    image: images.cat,
    caption: 'Chief Morale Officer, sunlight division.',
  },
]

export const motivations = [
  {
    title: 'Mental health, said out loud',
    body:
      'She began speaking publicly about workplace mental health because silence was costing people more than any program could recover. It remains the work she is proudest of.',
  },
  {
    title: 'Parental leave that actually holds',
    body:
      'Expanding paid parental leave at a 60,000-person company was a fiduciary argument and a personal one. She has since made the case publicly, with Fortune and JUST Capital.',
  },
  {
    title: 'The next generation of leaders',
    body:
      'Mentoring emerging people leaders \u2014 particularly women moving into fiduciary and board-facing roles \u2014 is how she measures the durability of her own career.',
  },
]

export const inquiryTypes = ['Board Seat', 'Strategic Advisory', 'Keynote / Panel']
